package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.SitePalette
import dev.lingvo.site.ds.SitePalettes
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import dev.lingvo.site.i18n.t

/**
 * The last thing on the page, and the only sentence on it that has to survive being read alone.
 *
 * A short invitation to try the file, review and export workflow with the visitor's own app.
 * The copy remains visible without entrance motion so the next step is immediately readable.
 *
 * **The call to action is an anchor, not a button.** It was an `LButton` with a
 * `router.navigateTo("/signup")` in its `onClick`, which renders a `<button>`: no href to hover, no
 * middle-click, no open-in-new-tab, no status bar — on the page's last and most important control,
 * and with the hero's own comment two files away spelling out that exact reasoning about the
 * identical action. `PublicActionLink` is the same 48px accent control drawn on an `<a>`, matching
 * the header and hero so every signup action retains native link behavior.
 *
 * **The primary control is tuned for paper, and this is the one band that is not paper.** Its label
 * is fine either way — `accentContrast` on `accent` is 6.93:1 in Day and 5.72:1 in Night. The focus
 * ring was not: drawn in the page accent it measures 2.70:1 against ink in Day and 2.95:1 against
 * paper in Night, so a keyboard visitor got an indicator below the 3:1 that WCAG 2.4.11 requires.
 * `PublicActionLink` carries the same [onInk] switch `LButton` does — it hands its ring variable the
 * `onInkPalette` accent, clearing 5.72:1 in Day and 6.25:1 in Night — so the fix survived the swap
 * rather than being a per-band override the next ink band would have to copy.
 *
 * The accent *fill* still reads 2.70:1 against the band, and that is left alone deliberately. WCAG
 * 1.4.11 asks that a control be identifiable; a filled button carrying a 6.93:1 label is. Repainting
 * the primary call to action for one band would trade a real brand decision for a number that is
 * not actually owed.
 */
@Composable
fun CloseBand() {
    Band(ground = BandGround.Ink, labelledBy = CLOSE_TITLE_ID) {
        H2(CloseHeadingStyle.toModifier().toAttrs { attr("id", CLOSE_TITLE_ID) }) {
            Text(t("marketing.close_band.start_with_one_file", "Start with one file."))
        }

        P(CloseSubStyle.toModifier().toAttrs()) {
            Text(
                t(
                    "marketing.close_band.translate_review_export",
                    "Translate one language, review the changes, and export the result back into your app.",
                ),
            )
        }

        Div(CloseActionsStyle.toModifier().toAttrs()) {
            PublicActionLink(
                path = "/signup",
                label = t("marketing.close_band.start_your_project", "Start your project"),
                // Keep the shared signup control's shape and ink-band focus treatment.
                modifier = Modifier.padding(leftRight = Space.XXXL),
                onInk = true,
            )
            P(CloseFinePrintStyle.toModifier().toAttrs()) {
                Text(t("marketing.close_band.use_existing_files", "Use your existing localization files."))
            }
        }
    }
}

private const val CLOSE_TITLE_ID = "close-title"

/**
 * The palette an ink band's *contents* are measured against.
 *
 * `BandInkStyle` does not paint a fixed dark colour — it paints `textPrimary` and writes in
 * `surfaceBase`, so the band is always the inverse of whatever ground the page is currently on. In
 * Day that is ink under paper text; in Night it is paper under ink text. A token picked from the
 * *current* palette is therefore exactly wrong here: Day's `textMuted` measures 3.27:1 on ink, and
 * Night's measures 2.74:1 on paper. The opposite palette's equivalent is the one that was tuned
 * against this ground — 6.16:1 and 5.21:1 respectively — which is the same argument
 * `BandLabelInkStyle` makes for the accent, generalised.
 */
private fun ColorMode.inkBandPalette(): SitePalette = when (this) {
    ColorMode.LIGHT -> SitePalettes.dark
    ColorMode.DARK -> SitePalettes.light
}

/**
 * One step above [BandHeadingStyle], and the only place on the page that takes it.
 *
 * [TypeScale.Hero] is a size the page spends once. Every other band tops out at `Display`, so the
 * close reads as the end of a document rather than as another section — the same device a printed
 * page uses when it sets its last line larger than its headings.
 *
 * Tracking tightens as the size grows because it has to: the display face's default spacing is
 * drawn for text sizes, and at 52px an untouched heading reads as though the words are drifting
 * apart. There is no token for it — `MicroTracking` is the scale's only tracking value and it goes
 * the other way, for uppercase mono at 10.5px.
 */
val CloseHeadingStyle = CssStyle {
    base {
        Modifier
            .displayFont()
            .fontSize(TypeScale.Hero)
            .fontWeight(700)
            .lineHeight(TypeScale.DisplayLineHeight)
            .letterSpacing((-2.2).px)
            .margin(0.px)
            // A measure, not a spacing step: 880px is roughly 48 characters at this size, which is
            // where the heading breaks into three lines instead of two ragged ones.
            .maxWidth(880.px)
            .color(colorMode.toSitePalette().surfaceBase)
    }
    // A phone gets the band heading size. Hero at 375px would set four words per line and turn a
    // closing sentence into six lines of scrolling.
    Breakpoint.ZERO { Modifier.fontSize(TypeScale.Heading).letterSpacing((-1.0).px) }
    Breakpoint.MD { Modifier.fontSize(TypeScale.Display).letterSpacing((-1.5).px) }
    Breakpoint.LG { Modifier.fontSize(TypeScale.Hero).letterSpacing((-2.2).px) }
}

/** The practical next step, secondary to the closing invitation. */
val CloseSubStyle = CssStyle.base {
    Modifier
        .fontSize(TypeScale.Body)
        .lineHeight(TypeScale.BodyLineHeight)
        // 620px holds this paragraph at about 70 characters a line — the top of the comfortable
        // range, and narrow enough that it does not run the full width of a 1256px band.
        .maxWidth(620.px)
        .margin(0.px)
        .color(colorMode.inkBandPalette().textMuted)
}

val CloseActionsStyle = CssStyle.base {
    Modifier
        .gap(Space.XL)
        .styleModifier {
            property("display", "flex")
            property("align-items", "center")
            // Supporting text drops below the button on narrow screens rather than truncating.
            property("flex-wrap", "wrap")
        }
}

/** Sits beside the button, so it drops the paragraph margin that would knock it off centre. */
val CloseFinePrintStyle = CssStyle.base {
    Modifier
        .fontSize(TypeScale.BodySmall)
        .margin(0.px)
        .color(colorMode.inkBandPalette().textMuted)
}
